import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import Organization from '../organizations/organization-component';
import UserList from '../users/user-list-component';
import ExternalApplicationUserList from '../external-application/external-app-users/external-app-user-list-component';
import ExternalAppUserForm from '../external-application/external-app-users/external-app-user-form-component';
import './admin.scss';
import DictionaryTenantList from '../dictionary/dictionary-tenant-list-component';
import DictionaryHistory from '../dictionary/dictionary-history/dictionary-history-component';
import UserDetail from '../users/user-detail-component';
import DictionaryList from '../dictionary/dictionary-list-component';
import { Box, Grid } from '@chakra-ui/react';
import { replace } from 'connected-react-router';

import {
  SecondaryNavigation,
  SecondaryNavigationOrientation,
  SecondaryNavigationVariants
} from '../../components/secondary-navigation/secondary-navigation';
import { ORGANIZATION_CONSTANTS } from '../organizations/organization-constants';
import { styles } from './Admin-Styles';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from '../../actions/organization';
import DictionaryValueList from '../dictionary/dictionary-value-list/dictionary-value-list-component';
import { DictionaryUpload } from '../dictionary/dictionary-upload/dictionary-upload';
import RequestInProgress from '../dictionary/request-in-progress/request-in-progress.component';
import { DictionaryValueDetail } from '../dictionary/dictionary-value-detail';
import { KeyColumnSelection } from '../dictionary/column-metadata/key-column-select';
import { CrossMapSelect } from '../dictionary/column-metadata/cross-map-select';
import { CrossMapEdit } from '../dictionary/column-metadata/cross-map-edit';
import ExternalApplicationListV2 from '../external-application/external-application-list-component';
import AuditLog from '../audit-log/audit-log-component';
import { hasResource } from '../../core/resources';
import Groups from '../groups/groups-component';
import AddGroup from '../groups/groups-add-form-component';
import GroupsDetail from '../groups/groups-detail-component';
import Features from '../features/features-component';
import { Action } from 'redux';
import ConfigurationManagement from '../configuration-management/configuration-management';

type SecondaryNavigationTab = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  matchPath: string;
  index: number;
};

export const Admin: React.FC<AppProps.AdminScreenProps> = (props: any) => {
  const {
    navigateExternalApplication,
    navigateDictionaries,
    navigateGroups,
    navigateFeatures,
    navigateConfigurations
  } = props;

  // global states: redux
  const ui = useSelector((state: CMx.ApplicationState) => state.ui);
  const { context } = useSelector((state: CMx.ApplicationState) => state.ui);

  // local states: built-in react hooks
  const [activeTab, setActiveTab] = useState<number>(0);
  const classes = styles();
  const history = useLocation();
  const isOrg = history?.pathname?.includes('orgs');
  const dispatch = useDispatch();

  // get enabled tabs (AdminTabs) from redux then determine default path
  const { AdminTabs } = ui;
  const defaultAdminTab = AdminTabs && AdminTabs.find(tab => tab.enabled);
  const defaultPath = defaultAdminTab
    ? `/cmx/${defaultAdminTab.path}`
    : '/cmx/admin/users/';

  const handleExternalAppClick = useCallback(() => {
    navigateExternalApplication(props.activeOrganization.id);
  }, [navigateExternalApplication, props.activeOrganization.id]);

  const handleDictionariesClick = useCallback(() => {
    const activeOrg = props.activeOrganization;

    const tenantItem: any = {
      tenantName: activeOrg.organizationName,
      key: activeOrg.tenantId,
      id: activeOrg.id,
      organizationCode: activeOrg.organizationCode
    };

    navigateDictionaries(tenantItem);
  }, [navigateDictionaries, props.activeOrganization]);

  const handleSubOrganizationsClick = useCallback(() => {
    const organization: CMxAPI.Organization = props.activeOrganization;
    const token: CMxCommonApp.BearerToken = props.auth;
    if (organization?.tenantId) {
        dispatch(replace(`/cmx/admin/orgs/${organization.id}/edit`));
        dispatch((navigate({ token, organization }) as any) as Action);
    }
  }, [dispatch, props.activeOrganization, props.auth]);

  const handleGroupsClick = useCallback(() => {
    navigateGroups(props.activeOrganization.id);
  }, [navigateGroups, props.activeOrganization.id]);

  const handleFeaturesClick = useCallback(() => {
    navigateFeatures('coder_config', props.activeOrganization.id);
  }, [navigateFeatures, props.activeOrganization.id]);

  const handleConfigurationsClick = useCallback(() => {
    navigateConfigurations(props.activeOrganization.id);
  }, [navigateConfigurations, props.activeOrganization.id]);

  const subnavs: SecondaryNavigationTab[] = useMemo(
    () => [
      {
        index: 0,
        label: ORGANIZATION_CONSTANTS.TENANTS,
        onClick: handleSubOrganizationsClick,
        matchPath: '/orgs'
      },
      {
        index: 1,
        label: ORGANIZATION_CONSTANTS.GROUPS,
        onClick: handleGroupsClick,
        matchPath: 'groups',
        disabled: !hasResource(
          context,
          ORGANIZATION_CONSTANTS.GROUPS.toLowerCase()
        )
      },
      {
        index: 2,
        label: ORGANIZATION_CONSTANTS.FEATURES,
        onClick: handleFeaturesClick,
        matchPath: 'features'
      },
      {
        index: 3,
        label: ORGANIZATION_CONSTANTS.DICTIONARIES,
        onClick: handleDictionariesClick,
        matchPath: 'dictionaries'
      },
      {
        index: 4,
        label: ORGANIZATION_CONSTANTS.EXTERNAL_APPS,
        onClick: handleExternalAppClick,
        matchPath: 'external-applications'
      },
      {
        index: 5,
        label: ORGANIZATION_CONSTANTS.CONFIGURATIONS,
        onClick: handleConfigurationsClick,
        matchPath: 'configurations',
        disabled: !hasResource(context, 'view-configuration')
      }
    ],
    [
      context,
      handleConfigurationsClick,
      handleDictionariesClick,
      handleExternalAppClick,
      handleFeaturesClick,
      handleGroupsClick,
      handleSubOrganizationsClick
    ]
  );

  // react hook: whenever location changes, sync active tab with the current pathname
  useEffect(() => {
    if (history?.pathname) {
      const nonTenantTab = subnavs.find(
        tab =>
          tab.matchPath !== '/orgs' && history.pathname.includes(tab.matchPath)
      );
      const tenantTab = subnavs.find(
        tab =>
          tab.matchPath === '/orgs' && history.pathname.includes(tab.matchPath)
      );
      // Check non-tenant tabs first since all tabs include '/orgs' in their path
      const matchedTab = nonTenantTab || tenantTab;

      if (matchedTab) {
        setActiveTab(matchedTab.index);
      }
    }
  }, [history, subnavs]);

  const handleTabsChange = (e: number) => {
    const tab = subnavs[e];
    if (tab && typeof tab.onClick === 'function' && !tab.disabled) {
      tab.onClick();
    } else {
      console.log(
        `Selected tabIndex ${e} is not present in organizations tabs constants`
      );
    }
  };

  return (
    <Box sx={classes.root}>
      {isOrg && (
        <Box sx={classes.tabs}>
          <SecondaryNavigation
            data={subnavs}
            variant={SecondaryNavigationVariants.UNSTYLED}
            orientation={SecondaryNavigationOrientation.VERTICAL}
            tabIndex={activeTab}
            handleTabsChange={handleTabsChange}
          />
        </Box>
      )}
      <Grid sx={isOrg ? classes.content : classes.usersContent}>
        <Switch>
          <Route exact path="/cmx/admin/">
            <Redirect to={defaultPath} />
          </Route>
          <Route path="/cmx/admin/orgs/" exact component={Organization} />
          <Route
            path="/cmx/admin/orgs/:id/(edit)"
            exact
            component={Organization}
          />
          <Route path="/cmx/admin/orgs/:id/groups" exact component={Groups} />
          <Route
            path="/cmx/admin/orgs/:id/groups/add"
            exact
            component={AddGroup}
          />
          <Route
            path="/cmx/admin/orgs/:id/groups/group/:id"
            exact
            component={GroupsDetail}
          />
          <Route
            path="/cmx/admin/orgs/:id/features"
            exact
            component={Features}
          />
          <Route
            path="/cmx/admin/orgs/:id/external-applications"
            exact
            component={ExternalApplicationListV2}
          />
          <Route
            path="/cmx/admin/orgs/:id/external-applications/:app"
            exact
            component={ExternalApplicationUserList}
          />
          <Route
            path="/cmx/admin/orgs/:id/external-applications/:app/users/add"
            exact
            component={ExternalAppUserForm}
          />
          <Route
            path="/cmx/admin/orgs/:id/external-applications/:app/users/edit"
            exact
            component={ExternalAppUserForm}
          />

          <Route
            path="/cmx/admin/orgs/:id/dictionaries/tenants"
            exact
            component={DictionaryTenantList}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries"
            exact
            component={DictionaryList}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/upload/new"
            exact
            component={DictionaryUpload}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id"
            exact
            component={DictionaryValueList}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/keyColumn"
            exact
            component={KeyColumnSelection}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/crossMaps"
            exact
            render={props => <CrossMapSelect {...props} viewOnly={false} />}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/crossMaps/view_only"
            exact
            render={props => <CrossMapSelect {...props} viewOnly={true} />}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/history"
            exact
            render={() => <DictionaryHistory />}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/metadata/crossMaps/:columnName/(edit)"
            exact
            component={CrossMapEdit}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/upload"
            exact
            component={DictionaryUpload}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/:requestIds"
            exact
            component={RequestInProgress}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/row/:caseId/(edit)"
            exact
            component={() => <DictionaryValueDetail addRow={false} />}
          />
          <Route
            path="/cmx/admin/orgs/:id/dictionaries/dictionary/:id/row/add"
            exact
            component={() => <DictionaryValueDetail addRow={true} />}
          />
          {/* Updated configuration routes */}
          <Route
            exact
            path="/cmx/admin/orgs/:orgId/configurations"
            render={routeProps => (
              <ConfigurationManagement
                {...routeProps}
                tenantId={props.activeOrganization.tenantId}
                tenantName={props.activeOrganization.organizationName}
              />
            )}
          />
          <Route
            exact
            path="/cmx/admin/orgs/:orgId/configurations/:configId"
            render={routeProps => (
              <ConfigurationManagement
                {...routeProps}
                tenantId={props.activeOrganization.tenantId}
                tenantName={props.activeOrganization.organizationName}
              />
            )}
          />

          <Route path="/cmx/admin/users/" exact component={UserList} />
          <Route path="/cmx/admin/users/:id/" exact component={UserDetail} />
          <Route path="/cmx/admin/auditLog/" exact component={AuditLog} />
        </Switch>
      </Grid>
    </Box>
  );
};

export default Admin;
